// DayDropdown.tsx

import React, { useState, useRef, useEffect } from 'react'
import {Container, 
        MonthSwitcher, 
        MonthButton, 
        MonthLabel,
        Menu, 
        Grid, 
        HeaderCell, 
        EmptyCell, 
        DayCell, 
        CalendarButton,
        CalendarIcon,
        DayBadge, } from '../../styles/timeline/DayDropdown.styles'
import {FiChevronLeft, FiChevronRight} from 'react-icons/fi'

export interface DayDropdownProps {
  selectedDate: Date
  onDateChange: (newDate: Date) => void
}

const DayDropdown: React.FC<DayDropdownProps> = ({ selectedDate, onDateChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [viewDate, setViewDate] = useState<Date>(selectedDate)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setViewDate(selectedDate)
  }, [selectedDate])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const changeMonth = (delta: number) => {
    const y = viewDate.getFullYear()
    const m = viewDate.getMonth()
    setViewDate(new Date(y, m + delta, 1))
  }

  const year = viewDate.getFullYear()
  const month = viewDate.getMonth()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const startIndex = new Date(year, month, 1).getDay() // 0 for Sunday, 1 for Monday, etc.

  const monthName = viewDate.toLocaleString('default', {
    month: 'long',
    year: 'numeric',
  })

  // --- THIS IS THE ROBUST SINGLE-GRID LOGIC ---
  const dayHeaders = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const dayCells: (number | null)[] = [
    ...Array(startIndex).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  const allCells = [...dayHeaders, ...dayCells];
  while (allCells.length % 7 !== 0) {
    allCells.push(null);
  }

  return (
    <Container ref={containerRef}>
      <CalendarButton open={isOpen} onClick={() => setIsOpen(o => !o)}>
        <CalendarIcon />
        <DayBadge>{selectedDate.getDate()}</DayBadge>
      </CalendarButton>
      {isOpen && (
        <Menu>
          <MonthSwitcher>
            <MonthButton onClick={() => changeMonth(-1)}>
              <FiChevronLeft />
            </MonthButton>
            <MonthLabel>{monthName}</MonthLabel>
            <MonthButton onClick={() => changeMonth(+1)}>
              <FiChevronRight />
            </MonthButton>
          </MonthSwitcher>

          <Grid>
            {allCells.map((cell, idx) => {
              if (idx < 7) { // First 7 items are the headers
                return <HeaderCell key={`header-${idx}`}>{cell}</HeaderCell>;
              }
              
              const day = cell as number | null;
              if (day === null) {
                return <EmptyCell key={`empty-${idx}`} />;
              }
              
              return (
                <DayCell
                  key={`day-${day}`}
                  selected={
                    viewDate.getFullYear() === selectedDate.getFullYear() &&
                    viewDate.getMonth() === selectedDate.getMonth() &&
                    day === selectedDate.getDate()
                  }
                  onClick={() => {
                    const newDate = new Date(
                      viewDate.getFullYear(),
                      viewDate.getMonth(),
                      day
                    );
                    onDateChange(newDate);
                    setIsOpen(false);
                  }}
                >
                  {day}
                </DayCell>
              );
            })}
          </Grid>
        </Menu>
      )}
    </Container>
  )
}
export default DayDropdown;